import { useState } from "react";
import { Box, Link } from "@mui/material";
import { ChevronRight } from "@mui/icons-material";
import { Tab, useCreatePath, useGetList, useGetOne, useRecordContext } from "react-admin";
import { useNavigate } from "react-router-dom";
import { SideDrawer } from "@/components/common/SideDrawer";
import { AuditLogBox } from "@/components/common/AuditLogBox";
import { ResourceName, type Claimant, type DocumentRead, type Policy } from "@/types";
import {
  DocumentsPanel,
  AttachmentPreview,
  usePinnedDocument,
  useRecordHasAttachments,
} from "@/components/common/DocumentsPanel";
import { featureFlags } from "@/utils/featureFlags";
import { StyledShow } from "@/components/ui/react-admin/StyledShow";
import { StyledTabbedShowLayout } from "@/components/ui/react-admin/StyledTabbedShowLayout";
import { StyledShowWithRail, useRailCollapsed } from "@/components/ui/react-admin/StyledShowWithRail";
import { TabContent } from "@/components/common/PageLayout";
import { useResourcePermissions } from "@/hooks/useResourcePermissions";
import { composeDocumentLabel } from "@/utils/formatters/documentLabel";
import { ClaimantSideDrawer } from "./components/ClaimantSideDrawer";
import { ClaimantDocumentUpload } from "./components/ClaimantDocumentUpload";
import { ClaimantHeader } from "./components/ClaimantHeader";
import { ClaimantClaimsTab } from "./components/ClaimantClaimsTab";
import { ClaimantDeductibleTab } from "./components/deductible/ClaimantDeductibleTab";
import { ClaimantDetailsTab } from "./components/details/ClaimantDetailsTab";
import { ClaimantRail, ClaimantRailCards, ClaimantSummaryBar } from "./components/rail/ClaimantRail";
import { LinkPolicyDialog } from "./components/LinkPolicyDialog";
import { ManageFamilyDialog } from "./components/ManageFamilyDialog";
import { PolicyProvider } from "./components/PolicyContext";
import { ClaimantPageProvider, useClaimantPage } from "./hooks/useClaimantPage";
import { RecomputeRefreshProvider } from "./hooks/useRecomputeRefresh";

// Right-drawer widths. Tasks (ClaimantSideDrawer) is the default SideDrawer 500;
// the document preview is portrait 600. One opens at a time: the preview shifts
// the content, the tasks drawer stacks beside it when both are open.
const TASKS_DRAWER_WIDTH = 500;
const PREVIEW_DRAWER_WIDTH = 600;

/**
 * Claimant page (Feature #7659): a left profile rail (identity, position against
 * the deductible, claims line, money lines, notes) beside a content column whose
 * tabs are Claims (default), Deductible, Details, Documents and Audit log. The
 * rail side and the shape are the claimant's page signature;
 * the claim page keeps its right action rail.
 * Design: docs/design/claimant-show-redesign (v4 page, v6 Deductible tab).
 */
export const ClaimantShow = () => (
  <StyledShow actions={false}>
    <ClaimantShowBody />
  </StyledShow>
);

export const ClaimantShowBody = () => {
  const record = useRecordContext<Claimant>();
  if (!record) return null;
  return (
    <PolicyProvider policies={record.policies || []}>
      <RecomputeRefreshProvider>
        <ClaimantPageProvider>
          <ClaimantPage />
        </ClaimantPageProvider>
      </RecomputeRefreshProvider>
    </PolicyProvider>
  );
};

const TabLabel = ({ text, count }: { text: string; count?: number }) => (
  <Box component="span" sx={{ display: "inline-flex", alignItems: "baseline", gap: 0.5 }}>
    {text}
    {count !== undefined && count > 0 ? (
      <Box component="span" sx={{ fontSize: "0.75rem", color: "text.secondary", fontWeight: 500 }}>
        {count}
      </Box>
    ) : null}
  </Box>
);

const ClaimantPage = () => {
  const { claimant, claimantId, familyUnit, claims } = useClaimantPage();
  const navigate = useNavigate();
  const createPath = useCreatePath();
  const railCollapsed = useRailCollapsed();
  const [tasksOpen, setTasksOpen] = useState(false);
  const [manageFamilyOpen, setManageFamilyOpen] = useState(false);
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);

  const { canList: canListDocuments } = useResourcePermissions(ResourceName.DOCUMENTS);
  const { canEdit: canEditClaimants } = useResourcePermissions(ResourceName.CLAIMANTS);

  // Document preview drawer state: the document **id** only, so no filename
  // lives in component state or (via the pin) in localStorage (PHI, ADR-086).
  const [previewDocId, setPreviewDocId] = useState<string | null>(null);
  const previewOpen = previewDocId !== null;
  const { pinnedId, setPinnedId } = usePinnedDocument(claimantId ? `claimant:${claimantId}` : undefined);
  const openDocumentPreview = (document: DocumentRead | null) => setPreviewDocId(document ? String(document.id) : null);
  const goToDocumentsTab = () =>
    void navigate(`${createPath({ resource: ResourceName.CLAIMANTS, id: claimantId, type: "show" })}/documents`);
  const { data: previewDoc } = useGetOne<DocumentRead>(
    ResourceName.DOCUMENTS,
    { id: previewDocId ?? "" },
    { enabled: !!previewDocId }
  );
  const previewTitle = previewDoc ? composeDocumentLabel(previewDoc) : "Document";
  const { count: attachmentCount } = useRecordHasAttachments("claimant", claimantId);

  // Link-another-policy is offered only when the client has a policy this claimant is not on.
  const linkedPolicyIds = new Set(claimant.policies?.map((p) => p.id) ?? []);
  const { data: clientPolicies = [] } = useGetList<Policy>(
    ResourceName.POLICIES,
    {
      filter: { client_id_ref: claimant.client_id_ref },
      pagination: { page: 1, perPage: 200 },
      sort: { field: "effective_start_date", order: "DESC" },
    },
    { enabled: !!claimant.client_id_ref && canEditClaimants }
  );
  const hasUnlinkedPolicies = clientPolicies.some((p) => !linkedPolicyIds.has(p.id));
  const onLinkPolicy = canEditClaimants && hasUnlinkedPolicies ? () => setLinkDialogOpen(true) : undefined;
  const onManageFamily = () => setManageFamilyOpen(true);

  const contentMarginRight = (tasksOpen ? TASKS_DRAWER_WIDTH : 0) + (previewOpen ? PREVIEW_DRAWER_WIDTH : 0);

  return (
    <>
      <Box sx={{ transition: "margin-right 0.3s ease", marginRight: `${contentMarginRight}px` }}>
        <StyledShowWithRail
          railSide="left"
          header={
            <ClaimantHeader
              onOpenTasks={() => setTasksOpen(true)}
              tasksOpen={tasksOpen}
              onLinkPolicy={onLinkPolicy}
              onManageFamily={canEditClaimants || claimant.subscriber_ref_id ? onManageFamily : undefined}
            />
          }
          rail={<ClaimantRail onManageFamily={onManageFamily} onLinkPolicy={onLinkPolicy} />}
          collapsedRail={<ClaimantSummaryBar onManageFamily={onManageFamily} onLinkPolicy={onLinkPolicy} />}
          railWidth="clamp(360px, 28%, 480px)"
          railWidthNarrow={280}
        >
          <StyledTabbedShowLayout>
            {/* Details first and default (Blake, 2026-10-29: people click in for
                relationships and eligibility). Claims keeps `path="claims"`: the rail's
                claims line, the claim page and its peek link there (#7660 AC6). */}
            <Tab label="Details">
              <ClaimantDetailsTab />
            </Tab>
            <Tab label={<TabLabel text="Claims" count={claims.length} />} path="claims">
              <ClaimantClaimsTab />
            </Tab>
            {/* Path kept as `deductibles`: task deep links and the claim page's peek point here. */}
            <Tab label="Deductible" path="deductibles">
              <ClaimantDeductibleTab />
            </Tab>
            <Tab
              label={<TabLabel text="Documents" count={attachmentCount + (claimant.source_documents?.length ?? 0)} />}
              path="documents"
            >
              <TabContent>
                {canListDocuments ? (
                  <DocumentsPanel
                    recordType="claimant"
                    recordId={claimantId}
                    sourceDocumentIds={claimant.source_documents}
                    selectedDocumentId={previewDocId}
                    onSelectDocument={openDocumentPreview}
                    pinnedDocumentId={pinnedId}
                    onPin={(d) => setPinnedId(String(d.id))}
                    onClearPin={() => setPinnedId(null)}
                    // Gate the add affordance on the deploy-safety flag (#6506).
                    renderUploader={
                      featureFlags.claimantAttachmentsEnabled
                        ? (onUploadComplete) => (
                            <ClaimantDocumentUpload
                              clientIdRef={claimant.client_id_ref as string | undefined}
                              onUploadComplete={onUploadComplete}
                            />
                          )
                        : undefined
                    }
                  />
                ) : (
                  <Box sx={{ color: "text.secondary", fontSize: "0.875rem", p: 2 }}>
                    You don&apos;t have permission to view documents.
                  </Box>
                )}
              </TabContent>
            </Tab>
            <Tab label="Audit log" path="audit">
              <AuditLogBox tableName={ResourceName.CLAIMANTS} />
            </Tab>
          </StyledTabbedShowLayout>
          {/* Below 700 px the rail is gone, so the notes card follows the tabs. */}
          {railCollapsed ? (
            <Box data-testid="rail-cards-collapsed" sx={{ display: "flex", flexDirection: "column", gap: 1.5, mt: 2 }}>
              <ClaimantRailCards />
            </Box>
          ) : null}
        </StyledShowWithRail>
      </Box>

      <ClaimantSideDrawer
        drawerExpanded={tasksOpen}
        toggleDrawer={() => setTasksOpen((v) => !v)}
        offsetRight={previewOpen ? PREVIEW_DRAWER_WIDTH : 0}
      />

      <SideDrawer
        title={previewTitle}
        width={PREVIEW_DRAWER_WIDTH}
        open={previewOpen}
        onClose={() => setPreviewDocId(null)}
        hideHandle
      >
        {previewDocId && (
          <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
            <Box sx={{ pb: 1.5 }}>
              <Link
                component="button"
                onClick={goToDocumentsTab}
                underline="hover"
                sx={{ display: "inline-flex", alignItems: "center", gap: 0.25, fontSize: "0.8125rem", fontWeight: 500 }}
              >
                View all documents
                <ChevronRight sx={{ fontSize: 18 }} />
              </Link>
            </Box>
            <Box sx={{ flex: 1, minHeight: 0 }}>
              <AttachmentPreview documentId={previewDocId} />
            </Box>
          </Box>
        )}
      </SideDrawer>

      <ManageFamilyDialog
        open={manageFamilyOpen}
        onClose={() => setManageFamilyOpen(false)}
        familyData={familyUnit.data}
        subscriberRefId={claimant.subscriber_ref_id}
        onMutationSuccess={familyUnit.refetch}
      />
      {canEditClaimants && <LinkPolicyDialog open={linkDialogOpen} onClose={() => setLinkDialogOpen(false)} />}
    </>
  );
};
